import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface DashboardSummary {
  readonly totals: {
    readonly assets: number;
    readonly published: number;
    readonly pendingApproval: number;
    readonly archived: number;
    readonly guidelines: number;
    readonly licenses: number;
  };
  readonly assetsByStatus: Record<string, number>;
  readonly assetsByCategory: Record<string, number>;
  readonly approvals: { readonly pendingStages: number };
  readonly compliance: { readonly validated: number; readonly passed: number; readonly passRate: number };
  readonly licenses: { readonly active: number; readonly expiringSoon: number; readonly expired: number };
  readonly assetsExpiringSoon: number;
  readonly recentDistributions: number;
}

const TOTALS: readonly { key: keyof DashboardSummary['totals']; labelKey: string }[] = [
  { key: 'assets', labelKey: 'm36.dashboard.total.assets' },
  { key: 'published', labelKey: 'm36.dashboard.total.published' },
  { key: 'pendingApproval', labelKey: 'm36.dashboard.total.pendingApproval' },
  { key: 'archived', labelKey: 'm36.dashboard.total.archived' },
  { key: 'guidelines', labelKey: 'm36.dashboard.total.guidelines' },
  { key: 'licenses', labelKey: 'm36.dashboard.total.licenses' },
];

/**
 * M36 — DAM Dashboard (§31). Read-only executive KPI roll-up for the Digital
 * Asset Management module: total asset counts and lifecycle split, pending
 * approval stages, brand-compliance pass rate, licence health (active /
 * expiring-soon / expired), assets expiring soon and recent distribution
 * volume, plus breakdowns of assets by lifecycle status and by category.
 * Tenant isolation is enforced server-side (LAW 4).
 */
export function DamDashboard(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: DashboardSummary }>(`/v1/dam/dashboard`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="dam-dashboard">
      <h1>{t('m36.dashboard.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="dam-totals" className="kpi-grid">
            {TOTALS.map(({ key, labelKey }) => (
              <div key={key} className="kpi-card" data-kpi={key}>
                <span className="kpi-label">{t(labelKey)}</span>
                <span className="kpi-value" aria-label={`total-${key}`}>{data.totals[key]}</span>
              </div>
            ))}
          </section>

          <dl aria-label="dam-health">
            <dt>{t('m36.dashboard.health.pendingStages')}</dt>
            <dd aria-label="pending-stages">{data.approvals.pendingStages}</dd>
            <dt>{t('m36.dashboard.health.complianceValidated')}</dt>
            <dd aria-label="compliance-validated">{data.compliance.validated}</dd>
            <dt>{t('m36.dashboard.health.compliancePassed')}</dt>
            <dd aria-label="compliance-passed">{data.compliance.passed}</dd>
            <dt>{t('m36.dashboard.health.compliancePassRate')}</dt>
            <dd aria-label="compliance-passrate">{number(data.compliance.passRate)}%</dd>
            <dt>{t('m36.dashboard.health.activeLicences')}</dt>
            <dd aria-label="licenses-active">{data.licenses.active}</dd>
            <dt>{t('m36.dashboard.health.licencesExpiring')}</dt>
            <dd aria-label="licenses-expiring">{data.licenses.expiringSoon}</dd>
            <dt>{t('m36.dashboard.health.expiredLicences')}</dt>
            <dd aria-label="licenses-expired">{data.licenses.expired}</dd>
            <dt>{t('m36.dashboard.health.assetsExpiring')}</dt>
            <dd aria-label="assets-expiring">{data.assetsExpiringSoon}</dd>
            <dt>{t('m36.dashboard.health.recentDistributions')}</dt>
            <dd aria-label="recent-distributions">{data.recentDistributions}</dd>
          </dl>

          <DataTable<{ key: string; count: number }>
            ariaLabel="assets-by-status"
            testId="assets-by-status"
            rowKey={(r) => r.key}
            rowDataAttrs={(r) => ({ 'data-status': r.key })}
            rows={Object.entries(data.assetsByStatus).map(([key, count]) => ({ key, count }))}
            columns={[
              { key: 'status', header: t('m36.dashboard.col.lifecycleStatus'), render: (r) => r.key },
              { key: 'count', header: t('m36.common.count'), render: (r) => r.count, cellAriaLabel: (r) => `status-${r.key}` },
            ]}
          />

          <DataTable<{ key: string; count: number }>
            ariaLabel="assets-by-category"
            testId="assets-by-category"
            rowKey={(r) => r.key}
            rowDataAttrs={(r) => ({ 'data-category': r.key })}
            rows={Object.entries(data.assetsByCategory).map(([key, count]) => ({ key, count }))}
            columns={[
              { key: 'category', header: t('m36.common.category'), render: (r) => r.key },
              { key: 'count', header: t('m36.common.count'), render: (r) => r.count, cellAriaLabel: (r) => `category-${r.key}` },
            ]}
          />
        </>
      )}
    </main>
  );
}
